/**
 * Utility functions for generating structured data
 */

import { getSocialProfileUrls } from './social-profiles';

interface Article {
  title: string;
  slug: string;
  excerpt?: string;
  content?: any;
  publishDate: string;
  tags?: string[];
  featuredImage?: any;
  author?: any;
}

/**
 * Generate Article structured data in JSON-LD format
 */
export function generateArticleSchema(article: Article, baseUrl = 'https://bemoi.com') {
  const articleUrl = `${baseUrl}/articles/${article.slug}`;
  const imageUrl = article.featuredImage 
    ? `https:${article.featuredImage.fields.file.url}` 
    : `${baseUrl}/bemoi_george.jpg`;
  
  const authorName = article.author?.fields?.name || 'Bemoi George';
  const authorUrl = article.author?.fields?.url || baseUrl;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': article.title,
    'description': article.excerpt || '',
    'image': imageUrl,
    'datePublished': article.publishDate,
    'dateModified': article.publishDate,
    'author': {
      '@type': 'Person',
      'name': authorName,
      'url': authorUrl
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'BEMOI',
      'logo': {
        '@type': 'ImageObject',
        'url': `${baseUrl}/icon-512.png`
      }
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': articleUrl
    },
    'keywords': article.tags?.join(', ') || '',
    'url': articleUrl
  };
}

/**
 * Generate Person structured data in JSON-LD format
 */
export function generatePersonSchema(baseUrl = 'https://bemoi.com') {
  const socialProfiles = getSocialProfileUrls();
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    'name': 'Bemoi George',
    'url': baseUrl,
    'image': `${baseUrl}/bemoi_george.jpg`,
    'jobTitle': 'DTC Growth & Performance Marketing Leader',
    'worksFor': {
      '@type': 'Organization',
      'name': 'skillshift'
    },
    'description': 'DTC growth strategist and performance marketing leader with 25+ years driving revenue across direct-to-consumer, marketplace, and enterprise brands. Expert in performance marketing, e-commerce acquisition, and revenue optimization.',
    'sameAs': socialProfiles,
    'knowsAbout': [
      'Direct-to-Consumer Marketing',
      'E-Commerce Strategy',
      'Performance Marketing',
      'Revenue Optimization',
      'Customer Acquisition',
      'SEO', 
      'Digital Marketing', 
      'Analytics', 
      'Conversion Rate Optimization'
    ]
  };
}

/**
 * Generate BreadcrumbList structured data in JSON-LD format
 */
export function generateBreadcrumbSchema(items: {name: string, url: string}[], baseUrl = 'https://bemoi.com') {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': items.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.name,
      'item': item.url.startsWith('http') ? item.url : `${baseUrl}${item.url}`
    }))
  };
}